import { default as Mui4Radio, RadioProps as Mui4RadioProps } from '@material-ui/core/Radio'
import { makeStyles } from '@material-ui/core/styles'
import AdjustIcon from '@material-ui/icons/Adjust'
import { default as Mui5Radio, RadioProps as Mui5RadioProps } from '@mui/material/Radio'
import { useComponentVersion } from '../hooks/useComponentVersion'
import { styled } from '../styles'

const StyledIcon = styled('span')<RadioProps>(({ theme, size, color }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  outline: `1.5px solid ${color === 'error' ? theme.palette.red[800] : theme.palette.neutralGrey[800]}`,
  backgroundColor: 'transparent',
  'input:hover + span &': {
    backgroundColor: 'transparent',
    outline: `1.5px solid ${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
  },
  'input:focus-visible + span &': {
    outline: `1.5px solid ${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    boxShadow: `0px 0px 0px 6px ${color === 'error' ? theme.palette.red[200] : theme.palette.blue[400]}`,
  },
  'input:disabled + span &': {
    outline: `1.5px solid ${color === 'error' ? theme.palette.red[300] : theme.palette.neutralGrey[500]}`,
    cursor: 'not-allowed',
  },
}))

const StyledCheckedIcon = styled('span')<RadioProps>(({ theme, size, color }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  outline: `1.5px solid ${color === 'error' ? theme.palette.red[800] : theme.palette.blue[800]}`,
  backgroundColor: 'transparent',
  '&:before': {
    display: 'block',
    width: 8,
    height: 8,
    backgroundColor: `${color === 'error' ? theme.palette.red[800] : theme.palette.blue[800]}`,
    borderRadius: '50%',
    content: '""',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  'input:hover + span &': {
    backgroundColor: 'transparent',
    outline: `1.5px solid ${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    '&:before': {
      backgroundColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    },
  },
  'input:focus-visible + span &': {
    outline: `1.5px solid ${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    boxShadow: `0px 0px 0px 6px ${color === 'error' ? theme.palette.red[200] : theme.palette.blue[400]}`,
    '&:before': {
      backgroundColor: `${color === 'error' ? theme.palette.red[1000] : theme.palette.blue[1000]}`,
    },
  },
  'input:disabled + span &': {
    outline: `1.5px solid ${color === 'error' ? theme.palette.red[300] : theme.palette.neutralGrey[500]}`,
    cursor: 'not-allowed',
    '&:before': {
      backgroundColor: `${color === 'error' ? theme.palette.red[300] : theme.palette.neutralGrey[500]}`,
    },
  },
}))

const StyledIconContainer = styled('span')<RadioProps>(({ theme, size, color }) => ({
  overflow: 'visible',
  display: 'flex',
  padding: 2,
  alignItems: 'center',
  justifyContent: 'center',
}))

const useStyles = makeStyles({
  root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    color: 'rgba(0, 0, 0, 0.25)',
  },
  checkedIcon: {
    '&.MuiSvgIcon-root': {
      color: '#1751D0',
    },
  },
  v2root: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  v2icon: {
    // Style for the default icon
    borderRadius: '50%',
    width: 20,
    height: 20,
    boxShadow: 'inset 0 0 0 1.5px #DCDCDE',
    backgroundColor: 'transparent',
    'input:hover ~ &': {
      backgroundColor: 'fff',
      boxShadow: 'inset 0 0 0 1.5px #6C92E4',
    },
  },
  v2checkedIcon: {
    // Style for the checked icon
    backgroundColor: '#1751D0',
    boxShadow: 'inset 0 0 0 1.5px #1751D0',
    '&:before': {
      display: 'block',
      width: 8,
      height: 8,
      backgroundColor: 'white',
      borderRadius: '50%',
      content: '""',
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    },
    'input:hover ~ &': {
      backgroundColor: '#113B98',
      boxShadow: 'inset 0 0 0 1.5px #113B98',
    },
    'input:focus ~ &': {
      outline: '1px solid white',
      boxShadow: '0px 0px 0px 3px #618DF2, 0px 0px 0px 4px #FFF',
    },
  },
})

export enum OsRadioVersion {
  V1 = 1, // OS UI 2.0
  V2 = 2, // MUI5 + Stratis
}

export type RadioProps = Omit<Mui5RadioProps, 'icon' | 'checkedIcon'>

export const Radio = (props: RadioProps) => {
  const classes = useStyles()
  const version = useComponentVersion('radio')
  let { color, checked, ...rest } = props

  switch (version) {
    case OsRadioVersion.V2:
      const currentRadioSize = 'medium'
      return (
        <Mui5Radio
          disableRipple
          checked={checked}
          size={currentRadioSize}
          color={color || 'primary'}
          icon={
            <StyledIconContainer>
              <StyledIcon size={currentRadioSize} color={color || 'primary'} />
            </StyledIconContainer>
          }
          checkedIcon={
            <StyledIconContainer>
              <StyledCheckedIcon size={currentRadioSize} color={color || 'primary'} />
            </StyledIconContainer>
          }
          {...rest}
        />
      )

    default:
      const color4: Mui4RadioProps['color'] = color === 'primary' || color === 'secondary' ? color : 'default'
      return (
        <Mui4Radio
          className={classes.root}
          disableRipple
          color={color4}
          checkedIcon={<AdjustIcon className={props.checked ? classes.checkedIcon : ''} />}
          {...rest}
        />
      )
  }
}
